<template>
  <div class="rich-text-editor">
    <div ref="toolbar" class="editor-toolbar">
      <!-- Text Formatting -->
      <span class="ql-formats">
        <select class="ql-header">
          <option value="1">標題 1</option>
          <option value="2">標題 2</option>
          <option value="3">標題 3</option>
          <option selected>正文</option>
        </select>
      </span>

      <!-- Basic Formatting -->
      <span class="ql-formats">
        <button class="ql-bold" title="粗體"></button>
        <button class="ql-italic" title="斜體"></button>
        <button class="ql-underline" title="底線"></button>
        <button class="ql-strike" title="刪除線"></button>
      </span>

      <!-- Lists -->
      <span class="ql-formats">
        <button class="ql-list" value="ordered" title="有序列表"></button>
        <button class="ql-list" value="bullet" title="無序列表"></button>
      </span>

      <!-- Quotes & Code -->
      <span class="ql-formats">
        <button class="ql-blockquote" title="引用"></button>
        <button class="ql-code-block" title="程式碼區塊"></button>
      </span>

      <!-- Colors -->
      <span class="ql-formats">
        <select class="ql-color" title="文字顏色"></select>
        <select class="ql-background" title="背景顏色"></select>
      </span>

      <!-- Alignment -->
      <span class="ql-formats">
        <select class="ql-align" title="對齊">
          <option selected></option>
          <option value="center"></option>
          <option value="right"></option>
          <option value="justify"></option>
        </select>
      </span>

      <!-- Links & Media -->
      <span class="ql-formats">
        <button class="ql-link" title="插入連結"></button>
        <button class="ql-image" title="插入圖片"></button>
      </span>

      <!-- Custom Buttons -->
      <span class="ql-formats">
        <button
          @click="showTagSelector"
          class="custom-tag-btn"
          title="插入 Tag"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
        </button>
        <button
          @click="showProductSelector"
          class="custom-product-btn"
          title="插入商品連結"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
        </button>
      </span>

      <!-- Clear Formatting -->
      <span class="ql-formats">
        <button class="ql-clean" title="清除格式"></button>
      </span>
    </div>

    <div ref="editor" class="editor-content"></div>

    <!-- Tag Selector Modal -->
    <div v-if="showTagModal" class="modal-overlay" @click="closeTagModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>選擇 Blog Tag</h5>
          <button @click="closeTagModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <input
            v-model="tagSearch"
            type="text"
            class="form-control mb-3"
            placeholder="搜尋 tag..."
          />
          <div class="tag-list">
            <div
              v-for="tag in filteredTags"
              :key="tag.id"
              @click="insertTag(tag)"
              class="tag-item"
            >
              <span class="tag-name">{{ tag.name }}</span>
              <span class="tag-slug">{{ tag.slug }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Selector Modal -->
    <div
      v-if="showProductModal"
      class="modal-overlay"
      @click="closeProductModal"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>選擇商品</h5>
          <button @click="closeProductModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <input
            v-model="productSearch"
            type="text"
            class="form-control mb-3"
            placeholder="搜尋商品..."
          />
          <div class="product-list">
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              @click="insertProductLink(product)"
              class="product-item"
            >
              <img
                v-if="product.image_url"
                :src="product.image_url"
                class="product-thumb"
              />
              <div class="product-info">
                <div class="product-name">{{ product.name }}</div>
                <div class="product-code">{{ product.product_code }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import request from "@/utils/request";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "開始撰寫內容...",
  },
});

const emit = defineEmits(["update:modelValue"]);

const editor = ref(null);
const toolbar = ref(null);
let quillInstance = null;

// Tag selector
const showTagModal = ref(false);
const tagSearch = ref("");
const availableTags = ref([]);

// Product selector
const showProductModal = ref(false);
const productSearch = ref("");
const availableProducts = ref([]);

// Filtered lists
const filteredTags = computed(() => {
  if (!tagSearch.value) return availableTags.value;
  const search = tagSearch.value.toLowerCase();
  return availableTags.value.filter(
    (tag) =>
      tag.name.toLowerCase().includes(search) ||
      tag.slug.toLowerCase().includes(search)
  );
});

const filteredProducts = computed(() => {
  if (!productSearch.value) return availableProducts.value.slice(0, 20);
  const search = productSearch.value.toLowerCase();
  return availableProducts.value
    .filter(
      (product) =>
        product.name.toLowerCase().includes(search) ||
        product.product_code.toLowerCase().includes(search)
    )
    .slice(0, 20);
});

// Initialize Quill
onMounted(() => {
  quillInstance = new Quill(editor.value, {
    theme: "snow",
    modules: {
      toolbar: toolbar.value,
    },
    placeholder: props.placeholder,
  });

  // Set initial content
  if (props.modelValue) {
    quillInstance.root.innerHTML = props.modelValue;
  }

  // Listen for text changes
  quillInstance.on("text-change", () => {
    emit("update:modelValue", quillInstance.root.innerHTML);
  });

  // Custom image handler
  quillInstance.getModule("toolbar").addHandler("image", function () {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*,.heic,.heif");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const range = quillInstance.getSelection();
          quillInstance.insertEmbed(range.index, "image", e.target.result);
        };
        reader.readAsDataURL(file);
      }
    };
  });

  // Fetch tags and products
  fetchTags();
  fetchProducts();
});

// Watch for external changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (quillInstance && newValue !== quillInstance.root.innerHTML) {
      quillInstance.root.innerHTML = newValue;
    }
  }
);

// Fetch blog tags
const fetchTags = async () => {
  try {
    const response = await request.get("/api/admin/blog/tags");
    availableTags.value = response.data.data || response.data;
  } catch (error) {
    console.error("Failed to fetch tags:", error);
  }
};

// Fetch products
const fetchProducts = async () => {
  try {
    const response = await request.get("/api/admin/products", {
      params: { per_page: 100 },
    });
    availableProducts.value = response.data.data || [];
  } catch (error) {
    console.error("Failed to fetch products:", error);
  }
};

// Tag selector methods
const showTagSelector = () => {
  showTagModal.value = true;
};

const closeTagModal = () => {
  showTagModal.value = false;
  tagSearch.value = "";
};

const insertTag = (tag) => {
  const range = quillInstance.getSelection();
  if (range) {
    const tagHtml = `<a href="/blog?tag=${tag.slug}" class="blog-tag" data-tag-id="${tag.id}">#${tag.name}</a>`;
    quillInstance.clipboard.dangerouslyPasteHTML(range.index, tagHtml);
  }
  closeTagModal();
};

// Product selector methods
const showProductSelector = () => {
  showProductModal.value = true;
};

const closeProductModal = () => {
  showProductModal.value = false;
  productSearch.value = "";
};

const insertProductLink = (product) => {
  const range = quillInstance.getSelection();
  if (range) {
    const productHtml = `<a href="/products/${product.id}" class="product-link" data-product-id="${product.id}">${product.name}</a>`;
    quillInstance.clipboard.dangerouslyPasteHTML(range.index, productHtml);
  }
  closeProductModal();
};

onBeforeUnmount(() => {
  if (quillInstance) {
    quillInstance.off("text-change");
    // Remove observer if possible? Quill 1.x logic mostly relies on container removal.
    // Setting to null helps garbage collection but doesn't stop immediate observers.
    // Disabling might help ensure no new events are processed.
    if (quillInstance.disable) {
      quillInstance.disable();
    }
    quillInstance = null;
  }
});
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
  overflow: hidden;
}

.editor-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #d2d6da;
  padding: 0.5rem;
}

.editor-content {
  min-height: 300px;
  background: white;
}

.editor-content :deep(.ql-editor) {
  min-height: 300px;
  font-size: 16px;
  line-height: 1.6;
}

.editor-content :deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #adb5bd;
}

/* Custom buttons */
.custom-tag-btn,
.custom-product-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #444;
  border-radius: 4px;
}

.custom-tag-btn:hover,
.custom-product-btn:hover {
  background: #e9ecef;
  color: #000;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

/* Tag list */
.tag-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-item {
  padding: 0.75rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tag-item:hover {
  background: #f8f9fa;
  border-color: #0d6efd;
}

.tag-name {
  font-weight: 600;
  color: #212529;
}

.tag-slug {
  font-size: 0.875rem;
  color: #6c757d;
}

/* Product list */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.product-item {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.product-item:hover {
  background: #f8f9fa;
  border-color: #0d6efd;
}

.product-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.25rem;
}

.product-code {
  font-size: 0.875rem;
  color: #6c757d;
}

/* Responsive */
@media (max-width: 768px) {
  .editor-toolbar {
    overflow-x: auto;
  }

  .modal-content {
    width: 95%;
    max-height: 90vh;
  }
}
</style>
